import { useAccess } from '@/common/hooks/useAccess';
import { useQuery } from '@tanstack/react-query';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { isUserEligibleForQaListPage } from '../../common/actionHelper';
import { Loading } from '../../common/components/Loading';
import { Spacer } from '../../common/components/v2';
import { FeatureFlagsContext } from '../../common/featureFlagsWrapper';
import { ExistingPolicies } from '../detail/ExistingPolicies';
import { ITask } from '../tasks/interfaces/ITask';
import { Actions } from './Actions';
import { AutoMedicalClaimsDetail } from './AutoMedicalClaimsDetail';
import { Decision } from './Decision';
import { DisclosureDetail } from './DisclosureDetail';
import { PolicyData } from './dtos/policyData.dto';
import {
  fetchEvidenceList,
  fetchMIBCodeBackData,
  getExistingPolicies,
  getPolicy,
} from './helpers';
import { MIBCodebackDetail } from './MIBCodebackDetail';
import { MIBDetail } from './MIBDetail';
import { MVRDetail } from './MVRDetail';
import { Overview } from './Overview';
import { QACase } from './QACase';
import { RuleResults } from './RuleResults';
import { RXDetail } from './RXDetail';
import { SherlockDetail } from './SherlockDetail';
import styles from './styles.module.scss';
import { TRLDetail } from './TRLDetail';

export const PPWBPolicy = () => {
  const { id: policyId } = useParams<{ id: string }>();

  const location = useLocation();
  const { flags } = useContext(FeatureFlagsContext);

  const [policyData, setPolicyData] = useState<PolicyData | null>(null);
  const [policyReady, setPolicyReady] = useState(false);

  const [decisionsForRuleResult, setDecisionsForRuleResult] = useState<
    { [key: string]: any }[] | undefined
  >(undefined);

  const [disclosureId, setDisclosureId] = useState(0);
  const [rxId, setRxId] = useState(0);
  const [trlId, setTrlId] = useState(0);
  const [mibId, setMibId] = useState(0);
  const [sherlockId, setSherlockId] = useState(0);
  const [mvrId, setMvrId] = useState(0);
  const [autoMedicalClaimsId, setAutoMedicalClaimsId] = useState(0);

  const isGen2 = Boolean(policyData?.meta?.uw_maestro_gen2_enabled);

  const [mibCodebackTask, setMibCodebackTask] = useState(
    null as unknown as ITask,
  );

  useEffect(() => {
    (async () => {
      if (!policyId) {
        return;
      }
      const result = await fetchMIBCodeBackData(policyId);
      if (result) {
        setMibCodebackTask(result);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!policyId) {
        return;
      }
      // Update the document title using the browser API
      document.title = `Product Performance Work Bench`;
      const policyFromState: any = location.state;
      let policy: any;
      if (!policyFromState) {
        policy = await getPolicy(policyId);
      } else {
        policy = policyFromState.policy;
      }

      setPolicyData(policy);
      if (!policy) {
        return;
      }

      const evidenceList = await fetchEvidenceList(
        policy.product,
        policy.carrier,
        policyId,
      );

      if (evidenceList) {
        evidenceList.forEach(e => {
          if (
            e.evidenceType === 'qe_disclosure' ||
            (e.evidenceStatus === 'FULFILLED' &&
              e.evidenceStatusInfo === 'SUCCESS')
          ) {
            switch (e.evidenceType) {
              case 'RX':
                setRxId(e.id);
                break;
              case 'TRL':
                setTrlId(e.id);
                break;
              case 'MIB':
                setMibId(e.id);
                break;
              case 'CRIMINAL_RECORDS':
                setSherlockId(e.id);
                break;
              case 'MVR':
                setMvrId(e.id);
                break;
              case 'qe_disclosure':
                setDisclosureId(e.id);
                break;
              case 'AUTO_MEDICAL_CLAIMS':
                setAutoMedicalClaimsId(e.id);
                break;
            }
          }
        });
      }
      setPolicyReady(true);
    })();
  }, []);

  const { data: existingPolicies, isLoading: existingPoliciesLoading } =
    useQuery({
      queryKey: ['existingPolicies', policyId],
      queryFn: () => {
        if (policyId) {
          return getExistingPolicies(policyId);
        }
        return null;
      },
    });

  if (!policyId || !policyReady || !policyData) {
    return (
      <>
        <Loading fullscreen={true} />
      </>
    );
  }

  const canAccessQASection = useAccess({
    permission: 'CREATE_QA_CASE',
    legacyCheck: isUserEligibleForQaListPage,
  });

  return (
    <div className={styles.Layout}>
      <div className={styles.ColumnDetail}>
        <Overview policy={policyData} />
        {flags.isQaWorkflowEnabled && canAccessQASection && (
          <QACase
            policyId={Number(policyId)}
            product={policyData.product}
            carrier={policyData.carrier}
          />
        )}
        <Decision
          policyId={policyId}
          product={policyData.product}
          carrier={policyData.carrier}
          subCategory={policyData.subCategory}
          setDecisionsForRuleResult={setDecisionsForRuleResult}
        />

        {!existingPoliciesLoading && existingPolicies && (
          <>
            <Spacer height={48} />
            <ExistingPolicies
              policyId={parseInt(policyId, 10)}
              policyNumber={policyData.carrierPolicyNumber}
              policies={existingPolicies}
              stackedCarrierPolicyNumbers={undefined}
              stackedTermPolicyIds={
                policyData.bundledPolicyDetails?.policyId
                  ? [policyData.bundledPolicyDetails?.policyId]
                  : undefined
              }
            />
          </>
        )}

        {decisionsForRuleResult && decisionsForRuleResult.length > 0 && (
          <RuleResults
            policyId={policyId}
            isGen2={isGen2}
            decisions={decisionsForRuleResult}
          />
        )}
        {disclosureId > 0 && (
          <DisclosureDetail
            product={policyData.product}
            carrier={policyData.carrier}
            policyId={policyId}
            evidenceId={disclosureId}
          />
        )}
        {rxId > 0 && (
          <RXDetail
            product={policyData.product}
            carrier={policyData.carrier}
            policyId={policyId}
            evidenceId={rxId}
          />
        )}
        {trlId > 0 && (
          <TRLDetail
            product={policyData.product}
            carrier={policyData.carrier}
            policyId={policyId}
            evidenceId={trlId}
          />
        )}
        {mvrId > 0 && (
          <MVRDetail
            product={policyData.product}
            carrier={policyData.carrier}
            policyId={policyId}
            evidenceId={mvrId}
          />
        )}
        {sherlockId > 0 && (
          <SherlockDetail
            product={policyData.product}
            carrier={policyData.carrier}
            policyId={policyId}
            evidenceId={sherlockId}
          />
        )}
        {mibId > 0 && (
          <MIBDetail
            product={policyData.product}
            carrier={policyData.carrier}
            policyId={policyId}
            evidenceId={mibId}
          />
        )}
        {autoMedicalClaimsId > 0 && (
          <AutoMedicalClaimsDetail
            product={policyData.product}
            carrier={policyData.carrier}
            policyId={policyId}
            evidenceId={autoMedicalClaimsId}
          />
        )}
        {mibCodebackTask && <MIBCodebackDetail task={mibCodebackTask} />}
        <Actions policyData={policyData} />
      </div>
    </div>
  );
};
